import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PageHeader } from "@/components/site/PageHeader";
import { QuoteFooter } from "@/components/site/QuoteFooter";
import { Reveal } from "@/components/site/motion";
import { BLOG } from "@/components/site/data";
import { Seo } from "@/components/site/Seo";

export default function BlogPage() {
  const [feature, ...rest] = BLOG;
  return (
    <>
      <Seo
        path="/blog"
        title="Blog"
        description="Guides, compliance explainers and practical advice on solar hot water and solar roof ventilation for Australian homes and buildings."
      />
      <PageHeader
        overline="Journal"
        title="The SolarArk blog."
        subtitle="Guides, compliance explainers and practical advice on solar hot water and roof ventilation for Australian homes and buildings."
      />

      <section data-testid="blog-list" className="relative py-16 lg:py-24 bg-[#050505] border-t border-[#1c1c1c]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Featured */}
          <Reveal>
            <Link to={`/blog/${feature.slug}`} data-testid={`blog-card-${feature.slug}`} className="group grid lg:grid-cols-2 gap-4 rounded-3xl overflow-hidden border border-[#262626] bg-[#0d0d0d] hover:border-[#fcc41b]/40 transition-colors duration-500 mb-4">
              <div className="relative h-64 lg:h-auto overflow-hidden">
                <img src={feature.image} alt={feature.title} className="grayscale-img w-full h-full object-cover group-hover:scale-105" />
                <span className="absolute top-5 left-5 font-mono-eng text-[10px] tracking-wider uppercase text-black bg-[#fcc41b] rounded-full px-3 py-1">{feature.category}</span>
              </div>
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <div className="font-mono-eng text-xs text-zinc-500 mb-4">By {feature.author} · {feature.date} · {feature.read}</div>
                <h2 className="font-display font-semibold text-3xl lg:text-4xl text-white leading-tight mb-4">{feature.title}</h2>
                <p className="text-zinc-400 text-lg leading-relaxed mb-6">{feature.excerpt}</p>
                <span className="inline-flex items-center gap-2 text-[#fcc41b] font-display font-medium">
                  Read article <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
                </span>
              </div>
            </Link>
          </Reveal>

          {/* Rest */}
          <div className="grid md:grid-cols-3 gap-4">
            {rest.map((post, i) => (
              <Reveal key={post.slug} delay={i * 0.08}>
                <Link to={`/blog/${post.slug}`} data-testid={`blog-card-${post.slug}`} className="group flex flex-col rounded-3xl overflow-hidden border border-[#262626] bg-[#0d0d0d] hover:border-[#fcc41b]/40 transition-colors duration-500 h-full">
                  <div className="relative h-52 overflow-hidden">
                    <img src={post.image} alt={post.title} className="grayscale-img w-full h-full object-cover group-hover:scale-105" />
                    <span className="absolute top-4 left-4 font-mono-eng text-[10px] tracking-wider uppercase text-black bg-[#fcc41b] rounded-full px-3 py-1">{post.category}</span>
                  </div>
                  <div className="p-7 flex flex-col flex-1">
                    <div className="font-mono-eng text-xs text-zinc-500 mb-3">By {post.author} · {post.date}</div>
                    <h3 className="font-display font-semibold text-xl text-white leading-snug mb-3">{post.title}</h3>
                    <p className="text-zinc-400 leading-relaxed mb-5">{post.excerpt}</p>
                    <span className="mt-auto inline-flex items-center gap-2 text-[#fcc41b] font-display font-medium text-sm">
                      Read article <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <QuoteFooter blurb="Have a question the blog didn't answer? Get in touch and our team will point you to the right solution — no obligation." />
    </>
  );
}
